import { useMemo, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { useBrainStore } from '@/state/brainStore';
import DashboardKPITile from '@/components/DashboardKPITile';
import {
  buildOpsWorklist,
  OPS_SECTIONS,
  type OpsItem,
  type OpsSectionKey,
} from '@/domain/opsWorklist';
import OpsWorkModal from '@/components/ops/OpsWorkModal';
import OpsOverridesAudit from '@/components/ops/OpsOverridesAudit';
import { opsItemIsActionable } from '@/components/ops/opsActionable';
import { aud, customerName, simDateShort, simTimeFull, studioDisplay } from '@/utils/format';
import type { CustomerRecord } from '@/domain/types';

// Ops manager worklist screen (vault System Diagram 09). Layout is the APPROVED
// Option B: a badge tab-bar across the top, the active section's list below at
// full width - a close cousin of the Confirmations worklist. The shell (tv-81c)
// fixed the layout + honest empty states; tv-v4p wires the real per-feeder
// derivations from the store so every tab renders its live rows. Actions
// (tv-o1f), the override tab (tv-i0b) and reports (tv-4cn) land here next.

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export default function OpsWorklist() {
  const clockIso = useSimulationStore((s) => s.clockIso);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);

  const bookings = useBrainStore((s) => s.bookings);
  const overrideRequests = useBrainStore((s) => s.overrideRequests);
  const topupPlans = useBrainStore((s) => s.topupPlans);
  const deposits = useBrainStore((s) => s.deposits);
  const noRebookFlags = useBrainStore((s) => s.noRebookFlags);
  const payoutRecords = useBrainStore((s) => s.payoutRecords);
  const artists = useBrainStore((s) => s.artists);
  const customers = useBrainStore((s) => s.customers);

  const worklist = useMemo(
    () =>
      buildOpsWorklist({
        nowIso: clockIso,
        studioIds: assignedStudios,
        bookings,
        overrideRequests,
        topupPlans,
        deposits,
        noRebookFlags,
        payoutRecords,
        artists,
        customers,
      }),
    [
      clockIso,
      assignedStudios,
      bookings,
      overrideRequests,
      topupPlans,
      deposits,
      noRebookFlags,
      payoutRecords,
      artists,
      customers,
    ],
  );

  const [active, setActive] = useState<OpsSectionKey>(OPS_SECTIONS[0]!.key);
  const [actionItem, setActionItem] = useState<OpsItem | null>(null);
  const activeMeta = OPS_SECTIONS.find((s) => s.key === active)!;
  const activeItems = worklist.sections[active];

  const nameFor = (customerId: string): string => {
    const c: CustomerRecord | undefined = customers[customerId];
    return c ? customerName(c) : customerId.slice(-6);
  };

  const decisions = worklist.counts.overrides + worklist.counts.confirmations;
  const moneyAndDates =
    worklist.counts['topup-extensions'] +
    worklist.counts['payby-extensions'] +
    worklist.counts['unpaid-artists'];

  return (
    <section className="px-8 py-8" data-testid="ops-worklist">
      <header className="flex items-end justify-between max-w-6xl flex-wrap gap-6">
        <div>
          <p className="label-small-caps text-gold-bright">Ops worklist</p>
          <h1 className="heading-display text-3xl text-cream mt-2">Work the desk to zero</h1>
          <p className="font-serif text-cream-soft mt-3 max-w-[54ch]">
            Every flagged item that falls out of the automated flows lands here. Work each
            section to zero: approve, extend, chase, or cancel. Ops is the only role that can
            approve a no-deposit override or cancel a booking.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <DashboardKPITile
            label="Open items"
            value={worklist.counts.total.toString()}
            accent
            caption="across the worklist"
          />
          <DashboardKPITile
            label="Decisions"
            value={decisions.toString()}
            caption="overrides + confirmations"
          />
          <DashboardKPITile
            label="Money & dates"
            value={moneyAndDates.toString()}
            caption="extensions + unpaid"
          />
        </div>
      </header>

      {/* Option B - the section tab-bar runs across the top; the list sits below
          at full width. Each tab is a notification badge with its count. */}
      <nav
        className="mt-6 max-w-6xl flex items-center gap-2 flex-wrap border-b border-gold-dim/25 pb-3"
        data-testid="ops-tab-bar"
        aria-label="Ops worklist sections"
      >
        {OPS_SECTIONS.map((meta) => {
          const count = worklist.counts[meta.key];
          const isActive = meta.key === active;
          return (
            <button
              key={meta.key}
              type="button"
              onClick={() => setActive(meta.key)}
              data-testid={`ops-tab-${meta.key}`}
              aria-current={isActive || undefined}
              className={clsx(
                'inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-inter font-medium transition',
                isActive
                  ? 'border-gold-bright/60 text-gold-bright bg-gold-bright/10'
                  : 'border-gold-dim/40 text-cream-soft hover:border-gold-bright/50',
              )}
            >
              <span>{meta.label}</span>
              <span
                data-testid={`ops-badge-${meta.key}`}
                className={clsx(
                  'min-w-[18px] rounded-full px-1.5 py-0.5 text-center font-mono text-[10px] leading-none',
                  count > 0 ? 'bg-blood text-white' : 'bg-ink-black/40 text-cream-faint',
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </nav>

      <div className="mt-5 max-w-6xl" data-testid={`ops-section-${active}`}>
        <div className="flex items-baseline gap-2 mb-1">
          <h2 className="label-small-caps text-gold-bright">{activeMeta.label}</h2>
          <span className="font-mono text-[11px] text-cream-faint">{activeItems.length}</span>
          <span className="flex-1 h-px bg-gold-dim/30" />
          <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
            {activeMeta.feeder}
          </span>
        </div>
        <p className="font-serif text-xs text-cream-soft mb-3">{activeMeta.blurb}</p>

        {/* Overrides tab folds in the retired /approvals audit trail (tv-i0b):
            the sticky decline-flag banner + the resolved-history disclosure. */}
        {active === 'overrides' ? <OpsOverridesAudit /> : null}

        {activeItems.length === 0 ? (
          <div className="panel p-8 text-center" data-testid={`ops-empty-${active}`}>
            <p className="font-inter text-sm text-cream-soft">Nothing to action here right now.</p>
            <p className="font-inter text-xs text-cream-faint mt-1.5">
              This section is worked to zero.
            </p>
          </div>
        ) : (
          <ol className="flex flex-col gap-2" data-testid={`ops-list-${active}`}>
            {activeItems.map((item) => (
              <OpsRow key={item.id} item={item} nameFor={nameFor} onAction={setActionItem} />
            ))}
          </ol>
        )}
      </div>

      {actionItem ? (
        <OpsWorkModal item={actionItem} onClose={() => setActionItem(null)} />
      ) : null}
    </section>
  );
}

function Pill({ label, cls }: { label: string; cls?: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-widest',
        cls ?? 'bg-cream-soft/15 text-cream-soft',
      )}
    >
      {label}
    </span>
  );
}

function OpsRow({
  item,
  nameFor,
  onAction,
}: {
  item: OpsItem;
  nameFor: (id: string) => string;
  onAction: (item: OpsItem) => void;
}) {
  return (
    <li
      data-testid={`ops-row-${item.id}`}
      className="panel p-2.5 flex items-center gap-3 flex-wrap"
    >
      <OpsRowBody item={item} nameFor={nameFor} />
      {opsItemIsActionable(item) ? (
        <button
          type="button"
          onClick={() => onAction(item)}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-gold-bright/50 bg-gold-bright/10 px-3 py-1.5 text-xs text-gold-bright hover:bg-gold-bright/20 transition"
          data-testid={`ops-action-${item.id}`}
        >
          Action
        </button>
      ) : null}
    </li>
  );
}

function Line({ title, meta }: { title: ReactNode; meta: ReactNode }) {
  return (
    <div className="flex-1 min-w-[200px]">
      <p className="font-inter text-sm text-cream flex items-center gap-2 flex-wrap">{title}</p>
      <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1 flex items-center gap-1.5 flex-wrap">
        {meta}
      </p>
    </div>
  );
}

function OpsRowBody({ item, nameFor }: { item: OpsItem; nameFor: (id: string) => string }) {
  switch (item.kind) {
    case 'override':
      return (
        <>
          <Line
            title={
              <>
                {item.booking ? nameFor(item.booking.customerId) : 'No-deposit request'}
                <Pill label={`Requested by ${item.request.requestedBy}`} />
              </>
            }
            meta={
              <>
                {item.booking ? <span>{studioDisplay(item.booking.studioId)}</span> : null}
                <span className="normal-case tracking-normal text-cream-soft">
                  &ldquo;{item.request.note}&rdquo;
                </span>
              </>
            }
          />
          <span className="font-mono text-[11px] text-cream-faint">{item.ageLabel}</span>
        </>
      );
    case 'confirmation':
      return (
        <>
          <Line
            title={
              <>
                {nameFor(item.booking.customerId)}
                {item.artistAsked ? (
                  <Pill label="Artist asked" cls="bg-azure/12 text-azure" />
                ) : (
                  <Pill label="Escalated" cls="pill-blood" />
                )}
              </>
            }
            meta={
              <>
                <span>{studioDisplay(item.booking.studioId)}</span>
                <span>{simTimeFull(item.booking.start)}</span>
              </>
            }
          />
        </>
      );
    case 'topup-extension':
      return (
        <>
          <Line
            title={
              <>
                {nameFor(item.plan.customerId)}
                <Pill label="Inside 7 days" cls="pill-blood" />
              </>
            }
            meta={
              <>
                <span>{studioDisplay(item.plan.studioId)}</span>
                {item.booking ? <span>Appt {simDateShort(item.booking.start)}</span> : null}
                {item.payByIso ? <span>Pay-by {simDateShort(item.payByIso)}</span> : null}
              </>
            }
          />
        </>
      );
    case 'payby-extension':
      return (
        <>
          <Line
            title={<>{nameFor(item.plan.customerId)}</>}
            meta={
              <>
                <span>{studioDisplay(item.plan.studioId)}</span>
                <span>Pay-by {simDateShort(item.payByIso)}</span>
                {item.deposit ? <span>{aud(item.deposit.amountAud)} deposit</span> : null}
              </>
            }
          />
        </>
      );
    case 'unpaid-artist':
      return (
        <>
          <Line
            title={
              <>
                {item.artistName}
                <Pill label={aud(item.amountAud)} cls="pill-gold" />
              </>
            }
            meta={
              <>
                <span>{studioDisplay(item.studioId)}</span>
                <span>Owed for {simDateShort(item.dayIso)}</span>
              </>
            }
          />
        </>
      );
    case 'no-rebook':
      return (
        <>
          <Line
            title={<>{nameFor(item.flag.customerId)}</>}
            meta={
              <>
                <span>{studioDisplay(item.flag.studioId)}</span>
                <span className="normal-case tracking-normal text-cream-soft">
                  {item.flag.reason}
                </span>
              </>
            }
          />
        </>
      );
    case 'topup':
      return (
        <>
          <Line
            title={
              <>
                {nameFor(item.plan.customerId)}
                <Pill label={DOW[item.dayOfWeek] ?? ''} cls="bg-azure/12 text-azure" />
              </>
            }
            meta={
              <>
                <span>{studioDisplay(item.plan.studioId)}</span>
                <span>Due {simDateShort(item.dueIso)}</span>
                <span>{aud(item.instalment.amountAud)}</span>
              </>
            }
          />
        </>
      );
    case 'unanswered':
      return (
        <>
          <Line
            title={<>{item.customerName}</>}
            meta={
              <>
                <span>{studioDisplay(item.studioId)}</span>
                <span className="normal-case tracking-normal text-cream-soft">
                  {item.lastInbound.body}
                </span>
              </>
            }
          />
          <span className="font-mono text-[11px] text-cream-faint">
            {simTimeFull(item.lastInbound.timestamp)}
          </span>
        </>
      );
    default: {
      // Exhaustiveness guard: a new OpsItem kind must add a branch above.
      void (item satisfies never);
      return null;
    }
  }
}
